/**
 * GPS设备管理
 */
import React, { useState } from 'react';
import { Card, Form, Row, Col, Button, Input, Select, Space, message, Popconfirm } from 'antd';
import CommonTable from '@/components/commonTable';
import ExportButton from '@/components/exportButton';
import { useTable } from '@/utils/useTable';
import { formSearchGutter } from '@/constants/common';
import styles from '@/styles/pageContent.module.scss';
import OperateModal from './components/operateModal';
import {
  gpsDeviceListRequest,
  gpsDeviceListRequestPath,
  gpsDeviceDeleteRequest,
  gpsDeviceEditRequest
} from '@/service/gpsDevice';

const { Option } = Select;

const GpsDeviceManage = () => {
  const [searchParams, setSearchParams] = useState<any>({});
  const [form] = Form.useForm();
  const [operateInfo, setOperateInfo] = useState<{ type: string; data?: any } | null>(null);

  const { page, limit, loading, data, totalCount, onPageChange, getData } = useTable({
    requestFunc: gpsDeviceListRequest,
    initPageSize: 10,
    searchParams
  });

  // 处理搜索参数
  const operateSearchParams = (values: any, isExport?: boolean) => {
    const newParams: any = { ...values };
    newParams.deviceId = (values.deviceId || '').trim();
    newParams.plateNumber = (values.plateNumber || '').trim();
    if (isExport) {
      newParams.export = true;
    }
    return newParams;
  };

  // 搜索
  const onSearch = (values: any) => {
    const newSearchParams = operateSearchParams(values);
    setSearchParams(newSearchParams);
  };

  // 重置
  const onReset = () => {
    form.resetFields();
    setSearchParams({});
  };

  // 获取导出参数
  const getExportParams = () => {
    const values = form.getFieldsValue();
    return operateSearchParams(values, true);
  };

  // 删除
  const onDelete = async (id: number) => {
    const res = await gpsDeviceDeleteRequest(id);
    if (res.data?.code === 0) {
      message.success('删除成功');
      getData();
    }
  };

  // 解绑
  const onUnbind = async (item: any) => {
    try {
      const res = await gpsDeviceEditRequest({
        id: item.id,
        deviceId: item.deviceId,
        manufacturer: item.manufacturer,
        plateNumber: null as any,
      });
      if (res.data?.code === 0) {
        message.success('解绑成功');
        getData();
      }
    } catch (error) {
      message.error('解绑失败');
    }
  };

  const columns = [
    { title: '设备ID', dataIndex: 'id', width: 80 },
    { title: '设备SN', dataIndex: 'deviceId', width: 150 },
    { title: '厂家', dataIndex: 'manufacturer', width: 100 },
    { title: '绑定车辆', dataIndex: 'plateNumber', width: 120 },
    { title: '创建时间', dataIndex: 'createdAt', width: 180 },
    { title: '创建人', dataIndex: 'createByName', width: 100 },
    { title: '是否在线', dataIndex: 'onlineFlagStr', width: 100 },
    { title: '是否上线', dataIndex: 'goLiveFlagStr', width: 100 },
    { title: '最近在线时间', dataIndex: 'onlineTime', width: 180 },
    {
      title: '操作',
      dataIndex: 'operate',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, item: any) => {
        const hasBoundCar = !!item.plateNumber;
        return (
          <Space size={0}>
            <Button type="link" onClick={() => setOperateInfo({ type: 'edit', data: item })}>编辑</Button>
            {hasBoundCar && (
              <Popconfirm
                title="确定要解绑该设备的车辆吗？"
                onConfirm={() => onUnbind(item)}
              >
                <Button type="link">解绑</Button>
              </Popconfirm>
            )}
            <Popconfirm
              title={hasBoundCar ? '该设备已绑定车辆，请先解绑后再删除' : '确定要删除该设备吗？'}
              onConfirm={() => {
                if (!hasBoundCar) {
                  onDelete(item.id);
                }
              }}
              okText={hasBoundCar ? '知道了' : '确定'}
              cancelText="取消"
            >
              <Button type="link" danger>删除</Button>
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  return (
    <Card bordered={false} className={styles.fixPageContentBox}>
      <Form form={form} onFinish={onSearch} autoComplete="off">
        <Row gutter={formSearchGutter}>
          <Col span={4}>
            <Form.Item label="设备SN" name="deviceId">
              <Input placeholder="请输入设备SN" allowClear />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="车牌号" name="plateNumber">
              <Input placeholder="请输入车牌号（模糊搜索）" allowClear />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="是否在线" name="onlineFlag">
              <Select placeholder="请选择是否在线" allowClear>
                <Option value={1}>是</Option>
                <Option value={0}>否</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="是否上线" name="goLiveFlag">
              <Select placeholder="请选择是否上线" allowClear>
                <Option value={1}>是</Option>
                <Option value={0}>否</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item>
              <div className={styles.searchBox} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="primary" onClick={() => setOperateInfo({ type: 'add' })}>新增</Button>
                <Button type="primary" htmlType="submit">搜索</Button>
                <Button onClick={onReset}>重置</Button>
                <ExportButton
                  url={gpsDeviceListRequestPath}
                  fileName="GPS设备列表"
                  getParamsFunc={getExportParams}
                  requestMethod="POST"
                  permissionKey="/car-admin/gps-device-manage/export"
                />
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <CommonTable
        rowKey="id"
        columns={columns}
        dataSource={data}
        pagination={{
          total: totalCount,
          current: page,
          pageSize: limit,
          onChange: onPageChange,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '40', '50'],
          showQuickJumper: true,
          showTotal: (total: number) => `共 ${total} 条`
        }}
        bordered
        loading={loading}
        scroll={{ x: 1200, y: '100%' }}
      />
      {operateInfo && (
        <OperateModal
          data={operateInfo.data}
          type={operateInfo.type}
          closeModal={() => setOperateInfo(null)}
          reloadData={getData}
        />
      )}
    </Card>
  );
};

export default GpsDeviceManage;
